import "@radix-ui/themes/styles.css";
import React, { useEffect, useState } from 'react';
import { Theme } from '@radix-ui/themes';
import Present from "./present/present";
import Remote from "./present/remote";
import { client } from './utils/sanity';
import "./main.scss";
import {
    createBrowserRouter,
    RouterProvider,
} from "react-router-dom";
import { useMutation } from "@tanstack/react-query"; 

const Main = () => {
    // ---------- Component state ----------
    // Visual/typography settings coming from global settings document
    const [settings, setSettings] = useState({
        backgroundColor : "",
        fontFamily : "",
        color : "",
        fontSize : "0px"
    });

    // Current service document and slides list
    const [service, setService] = useState({});
    const [slides, setSlides] = useState([]);
    const [isMorning, setIsMorning] = useState(true);

    // ---------- Data fetching ----------
    const fetchData = async () => {
        // Fetch global settings, the most recent service, and present slides
        // concurrently to reduce total wait time.
        const [settingsData, serviceData, slidesData, eveningSlidesData] = await Promise.all([
            client.fetch("*[_type=='globalSettings'][active == true][0]"),
            client.fetch("*[_type=='service']|order(date desc)[0]"),
            client.fetch("*[_type=='present'&&isHidden==false]|order(orderRank asc)"),
            client.fetch("*[_type=='evening'&&isHidden==false]|order(orderRank asc)")
        ]);
        // Return the fetched data so onSuccess receives it
        return { settingsData, serviceData, slidesData , eveningSlidesData};
    };

    const {
        data,
        error,
        isError,
        isPending,
        isSuccess,
        status,
        failureCount,
        isIdle,
        mutate,
        mutateAsync,
        reset,
    } = useMutation({
        mutationFn: fetchData,
        onError: (error) => {
            console.error('Error when fetching data:', error);
        },
        onSuccess: (data) => {
            console.log('Successfully fetched data:', data);
            const { settingsData, serviceData, slidesData, eveningSlidesData } = data;
            setSettings({
                backgroundColor : isMorning
                    ? settingsData.backgroundColour
                    : settingsData.eveningBackgroundColour,
                fontFamily : settingsData.defaultFontFamily,
                color : settingsData.defaultFontColour,
                fontSize : `${settingsData.baseFontSize}px`
            });

            if(isMorning){
                songExpander(serviceData, slidesData);
            }else{
                songExpander(serviceData, eveningSlidesData);
            }
        },
    })

    useEffect(() => {
        // Trigger the mutation when component mounts
        mutate();
    }, [mutate]);

    // ---------- Helper functions (pure / IO) ----------
    /**
     * Fetch the file document referenced by an asset object and return
     * the text content at its URL.
     * @param {Object} asset - Sanity asset reference object { _ref }
     * @returns {Promise<string|undefined>} text content or undefined on error
     */
    const getFile = async (asset) => {
        try {
            const response = await client.fetch(
                `*[_id == "${asset._ref}"]`
            );
            const fileUrl = response[0].url;
            // Fetch the remote file and return its text
            const textContent = await fetch(fileUrl).then(res => res.text());
            return textContent;
        }
        catch(e){
            console.error(e);
        }
    }

    /**
     * Convert a song body represented as an array of paragraph strings into
     * the Sanity block structure expected by the app.
     * Each paragraph string is expected to contain a title line followed by
     * content lines separated by newline characters.
     * @param {string[]} body
     * @returns {Array<Object>} array of blocks with children spans
     */
    const processSongBody = (body) => {
        return body.map(par => {
            let line = par.split("\n");
            // first line is treated as the stanza/section name
            let bbody = line.slice(1,line.length);
            return {
                name: line.slice(0,1),
                _type: "block",
                children : bbody.map(bod => {
                    return {
                        text: bod+"\n",
                        _type: "span"
                    }
                })
            }
        })
    }
    
    const processTitlePage = (title, artist, ccli, license) => {
        return [
            {
                text: `${title}\n`,
                _type: "span",
                marks: ["strong"]
            },
            {
                text: `${artist}\n`,
                _type: "span",
                marks: ["small", "em"]  // Add small mark for smaller font
            },
            {
                text: `${ccli}\n`,
                _type: "span",
                marks: ["smaller"]  // Even smaller for last 2
            },
            {
                text: `${license}\n`,
                _type: "span",
                marks: ["smaller"]
            }
        ]
    }

    /**
     * Expand slides that reference song files using tokens in the slide text.
     * It looks for tokens like `$songId` (optionally with `%<num>` ranges)
     * and replaces them by loading the referenced file and creating new slides
     * for each stanza/section found.
     * NOTE: This function mutates `slides` and calls `setSlides` with the new
     * list; it intentionally preserves existing app behavior.
     */
    const songExpander = async (serviceData, slidesData) => {
        // Build a replacements map where keys are like `$fieldName`
        let replacements = {};
        Object.keys(serviceData).map(key => replacements["$"+key] = serviceData[key]);
        
        let newSlides = [...slidesData]; // Copy to avoid mutating original
        const promises = []; // Collect async operations

        slidesData.forEach((slide, i) => {
            if(slide.isSong){
                slide.content.forEach(line => {
                    // Find tokens such as $songRef or $songRef%2-3
                    let dataTags = line.children[0].text.match(/\$\w+(?:%\d+(?:-\d+)?)?/g);
                    if (dataTags) {
                        dataTags.forEach(data => {
                            if(replacements[data] 
                                && replacements[data]?.["_type"] 
                                && replacements[data]?.["_type"] === "file"
                            ){
                                // Push the async operation to promises array
                                promises.push(
                                    (async () => {
                                        // Load the referenced file and split stanzas by double newline
                                        let fileContent = await getFile(replacements[data].asset);
                                        let lines = fileContent.split("\n\n");
                                        let title = lines[0];
                                        // remove last element if empty (mirrors prior behaviour)
                                        let body = lines.slice(1, lines.length - 1);
                                        let processedBody = processSongBody(body);

                                        let credits = lines[lines.length-1];
                                        let artist = credits.split("\n")[0];
                                        let ccliNum = credits.split("\n")[1];
                                        let ccliLicense = credits.split("\n")[4];

                                        slide.content[0].children = processTitlePage(title, artist, ccliNum, ccliLicense);
                                        
                                        // For each stanza/section, create a new slide
                                        let song = [];
                                        processedBody.forEach((process, x) => {
                                            song.push({
                                                name: `${title} - ${process.name}`,
                                                orderRank: slide.orderRank,
                                                _type: "block",
                                                content: [process]
                                            });
                                        });
                                        return { song, index: i };
                                    })()
                                );
                            }
                        });
                    }
                });
            }
        });

        // Wait for all async operations to complete and get results
        const results = await Promise.all(promises);

        // Sort results by index in descending order to handle array shifting during insertions
        const sortedResults = results.sort((a, b) => b.index - a.index);

        // Insert the song slides after their respective parent slides
        sortedResults.forEach(({ song, index }) => {
            newSlides.splice(index + 1, 0, ...song);
        });

        setSlides(newSlides);
        setService(serviceData);
        console.log('Successfully updated data:', {slides: newSlides, serviceData});
    }

    // ---------- Router (views) ----------
    // Router uses the current `service` and `slides` to render either the
    // remote-control view (root) or the presentation view (`/present`).
    const router = createBrowserRouter([
        {
            path: "/",
            element: <Remote
                service={service}
                slides={slides}
                refresh={mutate}
                isMorning={isMorning}
                setIsMorning={setIsMorning}/>,
        },
        {
            path: "/present",
            element: <Present
                service={service}
                slides={slides}
                refresh={mutate}
                isMorning={isMorning}
                setIsMorning={setIsMorning}/>,
        }
    ]);

    // ---------- Render ----------
    return (
        <Theme 
            accentColor="bronze" 
            grayColor="sand" 
            panelBackground="translucent"
            size="1" 
            style={settings}>
                {
                    // Only render routes after slides and service are available
                    (slides.length > 0 && Object.keys(service).length > 0)
                        ? <RouterProvider router={router}/>
                        : ""
                }
        </Theme>
    );
}

export default Main;